/* slider.js — <snap-slider>: a dependency-free carousel that moves like the
   source site's Swipers.
   Progressive enhancement: without JS the track is a plain horizontal
   scroller (CSS scroll-snap), so content is always reachable.

   Markup contract (used by testimonial, media, comparison, gallery sections):

   <snap-slider data-align="start|center" data-drag="false" data-wheel data-loop>
     <div class="snap-slider__track" data-slider-track>
       <div class="snap-slider__slide" data-slider-slide>…</div>
       …
     </div>
     <button data-slider-prev>…</button>   (optional)
     <button data-slider-next>…</button>   (optional)
     <div class="slider-bullets" data-slider-bullets
          data-bullet-label="Go to slide [index] of [total]"></div>   (optional)
   </snap-slider>

   The element tags the current snap target with [data-active] and its
   neighbours with [data-prev]/[data-next] (the media slider reveals captions
   for the centered trio, like the source site). Bullets are generated, one
   per slide, and are keyboard-focusable buttons — pass the translated
   aria-label template on data-bullet-label ([index]/[total] placeholders,
   e.g. {{ 'accessibility.go_to_slide' | t: index: '[index]', total: '[total]' }}).

   RTL note: deltas are computed from getBoundingClientRect(), and the one
   place that writes positions directly (the drag and the glide) works in
   PHYSICAL scrollLeft, which grows rightward in every current engine — in RTL
   it just runs from 0 into the negatives.

   Gestures (see bindDrag): every carousel on the source site is a Swiper, and
   what makes those feel the way they do is not the scroll container but
   Swiper's gesture model — the strip follows a mouse, finger or pen 1:1, and
   on release SWIPER, not the browser, decides where it lands (a flick under
   300ms always moves exactly one slide, a slower drag moves only past half a
   slide) and glides there in a fixed 300ms. A native scroll-snap track does
   neither: a release re-snaps to the nearest slide in zero time, a touch
   fling coasts for 540–730ms and can carry two slides, and a flick shorter than
   half a slide goes nowhere. Those were measured side by side against the
   live site (browser-check-live-parity/), so the landing is taken over here
   for every pointer type, and the track carries touch-action: pan-y (base.css)
   exactly as .swiper-horizontal does. Opt out with data-drag="false"; a press
   that lands inside NO_DRAG_SELECTOR never starts a swipe, so native media
   controls keep their own pointer.

   data-loop is the opt-in for Swiper's `loop: true` (see rotate()): the strip
   has no ends, and it opens with slide 0 at the snap point and the tail of the
   list wrapped around to its left. Only the media slider and the PDP gallery
   ask for it — the two sections whose live Swiper passes loop: true.
   data-loop is the request; the element answers with [data-looping] once it
   has enough slides (3+) and a track that actually overflows, and that is
   what the centre gutters in base.css key off — so the section must publish
   --snap-slide-w / --snap-gap for them. A slider that is refused
   ([data-static], or two blocks) is a plain centred scroller.

   data-wheel is the opt-in for Swiper's mousewheel module (see bindWheel),
   and mirrors the source's two configurations: data-wheel="horizontal" is
   `mousewheel: { forceToAxis: true }` (only sideways wheel/trackpad intent
   moves the strip, a vertical wheel stays the page's), bare data-wheel is
   `mousewheel: true` (either axis, the PDP thumbnail strip). Either way one
   wheel gesture moves one slide, as Swiper does. A section whose source
   Swiper has no mousewheel leaves it off and the track scrolls natively.

   Video cards: <media-slide> wraps a <video> + [data-video-overlay] play
   button. Add data-rewind-on-pause to <media-slide> for the UGC behaviour
   (pausing hides the native controls and returns to the poster frame);
   without it a pause keeps the controls and the playback position. */

function rectCenter(rect) {
  return rect.left + rect.width / 2;
}

/* Swiper's touch parameters, read off the RUNNING instances on the live site
   (el.swiper.params — every carousel there leaves them at Swiper's defaults):
   - threshold: pixels of travel before a press becomes a swipe. Below it the
     shopper is picking a slide (the thumb buttons run goTo); the move that
     crosses it re-bases the gesture, so the strip starts following from there.
   - touchAngle: decided once, on the first sample 5px from the press; a
     gesture that starts steeper than this is a page scroll, never a swipe.
   - longSwipesMs / longSwipesRatio: the landing rule (see landing()).
   - resistanceRatio: how hard a non-looping strip resists being pulled past
     its first or last slide (see dragTo()). */
const SWIPE = {
  threshold: 5,
  touchAngle: 45,
  longSwipesMs: 300,
  longSwipesRatio: 0.5,
  resistanceRatio: 0.85,
};

/* Move a slide inside the track without tearing its subtree down.

   insertBefore is specified as remove-then-insert even when the parent does
   not change, and Chrome really does run both custom-element reactions for
   every descendant (measured: one same-parent insertBefore on a connected
   node = 1 disconnectedCallback + 1 connectedCallback). For a strip of media
   cards that is destructive — the removal pauses a playing <video>, and a
   YouTube/Vimeo <iframe> RELOADS, restarting whatever was on screen.

   moveBefore() is the state-preserving version: the subtree never leaves the
   document, so the iframe is NOT reloaded and focus stays put (both measured).
   What it does NOT buy is silence — the atomic-move spec skips the
   disconnected/connected pair only for elements that implement
   connectedMoveCallback, and <media-slide> does not, so it still gets one of
   each. Its idempotence guard is therefore needed on BOTH paths, not just the
   fallback. Where moveBefore is missing, or refuses the move (it throws when
   the two nodes' connectedness does not match), fall back to insertBefore. */
function moveInto(parent, node, before) {
  if (typeof parent.moveBefore === 'function') {
    try {
      parent.moveBefore(node, before);
      return;
    } catch (error) {
      /* HierarchyRequestError — fall through to the destructive path. */
    }
  }
  parent.insertBefore(node, before);
}

/* Elements that own the pointer themselves, so a press starting inside one is
   never the start of a swipe: native <video> controls (scrubbing),
   <model-viewer> (drag rotates the model), cross-origin <iframe> embeds (their
   events never reach us anyway, and capturing the pointer would break their
   own player). [data-no-drag] is the escape hatch for anything a section adds
   later. */
const NO_DRAG_SELECTOR = 'video, model-viewer, iframe, [data-no-drag]';

/* Swiper's `speed` default, which none of the source's eight configs overrides.
   Its wrapper animates on transition-property: transform with the CSS `ease`
   default, so every move — a bullet press, a wheel step, the landing after a
   swipe, the spring back from an edge — takes the same 300ms whatever the
   distance (measured on the live strips: 297–347ms from release to rest). A UA
   smooth scroll instead derives its duration from the delta and uses a curve
   of its own, and a native touch fling coasts for 540–730ms. */
const SLIDE_DURATION = 300;

/* cubic-bezier(0.25, 0.1, 0.25, 1) — the CSS `ease` curve. Newton-Raphson to
   invert x(t), then evaluate y. Five iterations is well past convergence for
   this curve; the derivative guard covers the flat ends. */
function easeCss(x) {
  const cx = 0.75;
  const bx = -0.75;
  const ax = 1;
  const cy = 0.3;
  const by = 2.4;
  const ay = -1.7;
  const sampleX = (t) => ((ax * t + bx) * t + cx) * t;
  const slopeX = (t) => (3 * ax * t + 2 * bx) * t + cx;
  let t = x;
  for (let i = 0; i < 5; i += 1) {
    const error = sampleX(t) - x;
    if (Math.abs(error) < 1e-5) break;
    const slope = slopeX(t);
    if (Math.abs(slope) < 1e-6) break;
    t -= error / slope;
  }
  return ((ay * t + by) * t + cy) * t;
}

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

class SnapSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-slider-track]');
    /* [data-static] means "nothing here can scroll", and both early returns
       below are exactly that case — but they bail before sync() ever runs, so
       it has to be set here. Styling guarded on :not([data-static]) (the
       media-slider's dimming) would otherwise promise an interaction that has
       no listeners behind it. */
    if (!this.track) {
      this.toggleAttribute('data-static', true);
      return;
    }
    /* querySelectorAll returns DOM order, which for a LOOPING strip is a
       rotation of the logical one — so re-reading it on a RE-connect would
       adopt the rotation as the new "block order", and every index the element
       hands out (slider:change, the editor's block lookup, bullets) would
       silently mean something else. Re-connecting the same element is not
       hypothetical: it is how the browser-check bootstraps force an
       overflowing strip. Keep the first reading as long as it still describes
       this track; a theme-editor section reload replaces the markup, so that
       element is a new one and falls through to a fresh read. */
    const remembered = this.slides;
    const stillOurs =
      Array.isArray(remembered) &&
      remembered.length > 0 &&
      remembered.every((slide) => slide.parentNode === this.track);
    this.slides = stillOurs ? remembered : Array.from(this.track.querySelectorAll('[data-slider-slide]'));
    if (this.slides.length < 2) {
      this.toggleAttribute('data-static', true);
      return;
    }

    this.bullets = this.querySelector('[data-slider-bullets]');
    this.prevButton = this.querySelector('[data-slider-prev]');
    this.nextButton = this.querySelector('[data-slider-next]');

    if (this.bullets) this.buildBullets();
    if (this.prevButton) this.prevButton.addEventListener('click', () => this.step(-1));
    if (this.nextButton) this.nextButton.addEventListener('click', () => this.step(1));

    this.onScroll = () => {
      window.clearTimeout(this.scrollTimer);
      this.scrollTimer = window.setTimeout(() => this.sync(), 80);
    };
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    /* Layout changes (breakpoint, rotation, late-loading media) move the snap
       point without always changing scrollLeft — at offset 0 the container
       re-snaps to the same position, so no scroll event ever fires and the
       index goes stale. Re-derive it from the track's own resizes; reusing
       onScroll gives the same 80 ms debounce and the same timer cleanup. */
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.onScroll);
      this.resizeObserver.observe(this.track);
    }

    /* Loop needs a slide to spare on either side of the centred one, which two
       slides cannot give: rotating them just swaps the pair back and forth. */
    this.loop = this.hasAttribute('data-loop') && this.slides.length >= 3;
    /* data-loop is the section's REQUEST; [data-looping] is what was granted,
       and it is the one the centre gutters in base.css key off. They are two
       different things: with two blocks there is no rotation to do, and with a
       track that does not overflow there is nothing to centre — in both cases
       an authored data-loop would otherwise open the section on half a screen
       of blank gutter. Set before the first sync(), because rotate() needs the
       gutters already in place to have anywhere to put the wrapped tail; sync()
       re-decides it on every resize from there. */
    this.toggleAttribute('data-looping', this.loop && this.overflows());

    const startIndex = Number(this.dataset.startIndex || 0);
    if (startIndex > 0 && !this.loop) {
      /* Keep the handle: the theme editor can dispatch shopify:block:select in
         the same batch as the section load, and the queued frame would then
         scroll away from the block the merchant just selected. */
      this.startFrame = window.requestAnimationFrame(() => {
        this.startFrame = 0;
        this.goTo(startIndex, 'auto');
      });
    }

    /* On by default (Swiper's allowTouchMove + simulateTouch, which every
       source-site slider runs with). data-drag="false" is the per-element
       opt-out and wires no pointer listeners at all. */
    if (this.dataset.drag !== 'false') this.bindDrag();
    if (this.hasAttribute('data-wheel')) this.bindWheel();

    this.sync();

    /* AFTER the first sync, never before: the centre gutters are keyed off
       :not([data-static]), so until sync() has ruled on overflow the track has
       no room to put anything left of slide 0 and the rotation would have
       nowhere to land. sync() also rotates, but only to hold the CURRENT slide
       centred — the opening position is this one command. */
    if (this.loop && !this.hasAttribute('data-static')) {
      this.startFrame = window.requestAnimationFrame(() => {
        this.startFrame = 0;
        this.goTo(startIndex, 'auto');
      });
    }
  }

  disconnectedCallback() {
    if (this.track) this.track.removeEventListener('scroll', this.onScroll);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.startFrame) window.cancelAnimationFrame(this.startFrame);
    window.clearTimeout(this.scrollTimer);
    this.cancelTween();
    this.unbindDrag();
    this.unbindWheel();
  }

  /* --- Swiping (default on; data-drag="false" opts out) --------------------
     One gesture model for mouse, touch and pen, Swiper's: follow the pointer
     1:1 from the moment it crosses SWIPE.threshold, then on release pick the
     landing slide with Swiper's own rule and glide there in SLIDE_DURATION.

     Touch used to be left to the browser, on the theory that native panning
     and momentum were the better feel. Measured against the live site they
     are not the SAME feel, which is the brief: a 380px swipe carried two
     slides where Swiper moves one, a slow 240px drag held for a beat landed
     two slides on (sync() rotated the loop under the finger, because
     this.dragging was never set for touch), and every fling coasted 540–730ms on
     the browser's own curve instead of Swiper's 300ms ease. The browser keeps
     what it is better at — vertical page panning, via touch-action: pan-y —
     and hands the horizontal axis over, as it does for a Swiper. */
  bindDrag() {
    this.resetDragState();
    this.overshoot = 0;

    this.onDragDown = (event) => {
      /* A stale swallow-the-click listener (a release that produced no click,
         see onDragUp) must never eat this interaction's click. */
      this.track.removeEventListener('click', this.onDragClick, true);
      /* Whatever the previous press left behind, this one starts clean. A
         glide in flight is deliberately NOT stopped here — Swiper lets a tap
         land mid-transition without interrupting it, and only a press that
         turns into a swipe takes the strip over (beginDrag). */
      this.resetDragState(!this.tweenFrame);
      /* A second finger never starts or steers a swipe. */
      if (!event.isPrimary) return;
      /* Left button only: the middle one owns autoscroll, the right one the
         context menu. */
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      /* Nothing to drag. Same condition as the [data-static] flag in sync(),
         re-read here because layout may have changed since the last sync. */
      if (!this.overflows()) return;
      /* The press landed on something that owns the pointer itself (a playing
         video's controls, a model-viewer, an embed). Swiping from there would
         steal the interaction the shopper actually started. Overlays that sit
         ON TOP of such media — media-slide's play button — are not matched, so
         a card at rest still swipes from anywhere on its poster. */
      if (event.target.closest && event.target.closest(NO_DRAG_SELECTOR)) return;
      this.dragPointer = event.pointerId;
      this.pressType = event.pointerType;
      this.pressX = event.clientX;
      this.pressY = event.clientY;
      this.lastX = event.clientX;
      this.pressRTL = this.isRTL;
      this.pressTime = performance.now();
      /* No preventDefault here: the press must still focus the thumb button
         it lands on, so the keyboard path keeps working (Swiper exempts
         buttons from its own touchStartPreventDefault for the same reason). */
    };

    this.onDragMove = (event) => {
      if (event.pointerId !== this.dragPointer) return;
      /* A mouse with no button held any more (bit 0 = left): the press ended
         somewhere we never heard about. Below the threshold there is no
         pointer capture yet, so a release outside the track goes to whatever
         is under the cursor and never reaches this listener — leaving
         dragPointer set, and the next button-less HOVER would be measured
         against that stale press and start a real drag. */
      if (event.pointerType === 'mouse' && !(event.buttons & 1)) {
        this.endDrag(event, this.dragging);
        return;
      }
      /* A finger is followed through its TouchEvents (onTouchMove), not its
         pointer events — see there. */
      if (this.touchId !== null) return;
      if (this.moveTo(event.clientX, event.clientY) && event.cancelable) event.preventDefault();
    };

    /* Touch goes through TouchEvents because that is what Swiper follows
       (its pointermove handler returns as soon as a touchstart has given it a
       touch id), and the two streams are NOT the same: Chrome holds back
       touchmove inside its touch-slop radius, pointermove it does not. So on
       the live site a finger's first ~12px never reach Swiper — a card
       tapped with a little jitter stays a tap, and a swipe re-bases its
       start a step later — while a pointer-driven strip saw every pixel and
       turned an 8px jitter into a whole slide. Listening to the same events
       Swiper does inherits the browser's own slop on every device, instead
       of guessing its size. Start and end stay on pointerdown/pointerup,
       which is also where Swiper takes them from. */
    this.onTouchStart = (event) => {
      if (this.dragPointer === -1 || this.pressType !== 'touch' || event.targetTouches.length !== 1) return;
      this.touchId = event.targetTouches[0].identifier;
    };
    this.onTouchMove = (event) => {
      if (this.dragPointer === -1 || this.touchId === null) return;
      const touch = Array.from(event.changedTouches).find((t) => t.identifier === this.touchId);
      if (touch) this.moveTo(touch.clientX, touch.clientY);
    };

    this.onDragUp = (event) => {
      if (!this.endDrag(event, this.dragging)) return;
      /* The click that follows a real swipe lands on whatever the pointer
         happens to rest on — a thumb (product.js would jump the main image
         there) or a card link. Swallow that one click in the capture phase,
         before it can reach anything: Swiper's preventClicks. A press that
         never passed the threshold is a tap and is left alone. */
      this.track.addEventListener('click', this.onDragClick, { capture: true, once: true });
      /* click is dispatched in the same task as this pointerup, so a 0 ms
         timeout lands after it either way and disarms the swallow. Without it
         a release that produced no click (a touch swipe never does; nor does a
         mouse let go outside the window) would leave it armed — and a keyboard
         Enter on a thumb fires click too, which must never be eaten. Handle
         kept so disconnecting in between cannot leave a callback holding the
         old track. */
      window.clearTimeout(this.dragClickTimer);
      this.dragClickTimer = window.setTimeout(() => {
        this.track.removeEventListener('click', this.onDragClick, true);
      }, 0);
    };

    /* pointercancel (the browser took the gesture for a page pan, or the OS
       for one of its own) and lostpointercapture (window blur, the context
       menu) land a swipe that was under way exactly like a release — a strip
       left between slides with snapping switched off is the one outcome that
       must never happen — but arm no click swallow: neither produces a click.
       endDrag ignores a pointerId that is no longer the live one, so the
       lostpointercapture that follows every pointerup is a no-op. */
    this.onDragCancel = (event) => {
      /* lostpointercapture BUBBLES. A touch pointer is implicitly captured by
         whatever it landed on (the slide's <img>), so the moment beginDrag
         moves capture to the track that element loses it — and its
         lostpointercapture arrived here and ended the swipe on the very move
         that started it (measured: every touch swipe died at the threshold).
         Only the track's own loss counts. */
      if (event.type === 'lostpointercapture' && event.target !== this.track) return;
      this.endDrag(event, this.dragging);
    };

    /* Native image dragging (every thumb is an <img>) would take the pointer
       away mid-swipe and fire pointercancel. Only while a press is live on
       the track — other pointers keep the native behaviour. */
    this.onDragImage = (event) => {
      if (this.dragPointer !== -1) event.preventDefault();
    };

    this.onDragClick = (event) => {
      event.stopPropagation();
      event.preventDefault();
    };

    this.track.addEventListener('pointerdown', this.onDragDown);
    this.track.addEventListener('pointermove', this.onDragMove);
    this.track.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.track.addEventListener('touchmove', this.onTouchMove, { passive: true });
    this.track.addEventListener('pointerup', this.onDragUp);
    this.track.addEventListener('pointercancel', this.onDragCancel);
    this.track.addEventListener('lostpointercapture', this.onDragCancel);
    this.track.addEventListener('dragstart', this.onDragImage);
  }

  /* One move of the live press, from whichever stream carries it. This is
     Swiper's onTouchMove, in Swiper's order (read off the bundle the live
     site runs). The start point it measures from is the press until the
     threshold is crossed, then wherever it was last re-based. Returns true
     when the strip followed. */
  moveTo(clientX, clientY) {
    const previousX = this.lastX;
    this.lastX = clientX;
    const dx = clientX - (this.dragging ? this.dragOriginX : this.pressX);
    const dy = clientY - (this.dragging ? this.dragOriginY : this.pressY);
    /* Nothing happens within `threshold` of the start point — including,
       after every re-base, within that distance of the new one. */
    if (Math.hypot(dx, dy) < SWIPE.threshold) return false;
    if (!this.axisDecided) {
      this.axisDecided = true;
      /* Steeper than touchAngle: a page scroll that happens to start on the
         strip. Under touch-action: pan-y the browser is already panning the
         page (and will cancel this pointer); for a mouse it is simply not a
         swipe. */
      if ((Math.atan2(Math.abs(dy), Math.abs(dx)) * 180) / Math.PI > SWIPE.touchAngle) {
        this.resetDragState(!this.tweenFrame);
        return false;
      }
    }
    /* Two directions, as Swiper keeps them: swipeDirection is the sign of the
       travel from the start point (it decides the landing), and
       touchesDirection the sign of THIS move alone. Logical, so mirrored in
       RTL. */
    const flip = this.pressRTL ? -1 : 1;
    const diff = dx * flip;
    const turned = this.touchesDirection;
    this.swipeDirection = diff > 0 ? 'prev' : 'next';
    this.touchesDirection = (clientX - previousX) * flip > 0 ? 'prev' : 'next';
    if (!this.dragging) {
      if (Math.abs(dx) <= SWIPE.threshold) return false;
      this.beginDrag(clientX, clientY);
      this.diff = 0;
      return false;
    }
    this.diff = diff;
    /* Swiper's loopSwapReset. On a LOOPING strip, a move that turns back
       against the previous one re-bases the gesture on the spot and is not
       applied: the strip holds still for that one move and the travel is
       measured afresh from here. It is why, on the live media strip, 100px
       out and 100px back does not bring the card home — it rests ~21px short
       of where it started, and a quick release there lands back on the SAME
       card, where a straight replay of the moves (and a strip that does not
       loop) lands on the previous one. Measured on both sides with identical
       pointer streams before this was copied over. */
    if (this.loop && turned && turned !== this.touchesDirection && Math.abs(diff) >= 1) {
      this.dragOriginX = clientX;
      this.dragOriginY = clientY;
      this.dragOriginScroll = this.track.scrollLeft;
      this.loopSwapReset = true;
      return false;
    }
    this.dragTo(clientX);
    this.dragX = clientX;
    return true;
  }

  /* The press just crossed the threshold: from here the strip follows. */
  beginDrag(clientX, clientY) {
    this.dragging = true;
    /* Take the strip over from a glide where it stands — without handing
       snapping back first, which would re-snap it on the spot. */
    this.stopTweenFrame();
    /* A press during an edge spring-back folds what is left of it into this
       drag instead of dropping it (see dragTo for the curve being inverted). */
    const shown = this.stopBounce();
    const over = shown ? -Math.sign(shown) * Math.pow(Math.abs(shown) + 1, 1 / SWIPE.resistanceRatio) : 0;
    /* Capture, so a pointer that leaves the track — or the window — keeps
       delivering move/up here and the swipe can always be ended. */
    this.track.setPointerCapture(this.dragPointer);
    this.setAttribute('data-dragging', '');
    /* Sliders whose slides are mostly text (testimonials, comparison cards)
       start a native text selection on the way to the threshold, and the
       highlight then follows the drag across the whole strip. user-select:none
       (base.css, keyed off [data-dragging]) stops it GROWING but cannot undo
       what is already selected, so collapse it — and only when it is inside
       this track, never a selection the shopper made elsewhere on the page. */
    const selection = document.getSelection();
    if (selection && !selection.isCollapsed && this.track.contains(selection.anchorNode)) {
      selection.removeAllRanges();
    }
    /* Mandatory snapping would pull the track back to the nearest snap point
       after every write, so it stands down until the landing glide is over. */
    this.track.style.scrollSnapType = 'none';
    const max = this.track.scrollWidth - this.track.clientWidth;
    this.dragRange = this.isRTL ? { min: -max, max: 0 } : { min: 0, max };
    /* Re-based on the move that crossed the threshold, as Swiper does: the
       threshold's own few pixels are never applied. */
    this.dragOriginX = clientX;
    this.dragOriginY = clientY;
    this.dragOriginScroll = this.track.scrollLeft + over;
    this.dragX = clientX;
    /* landing() reads it, and a swipe can be landed before its first dragTo
       (a cancel on the move that crossed the threshold). */
    this.overshoot = over;
  }

  /* Follow the pointer: content moves with it, so the scroll goes the other
     way. Physical coordinates throughout, which makes the same arithmetic hold
     in RTL (scrollLeft runs negative there, and dragRange says so). */
  dragTo(clientX) {
    let want = this.dragOriginScroll - (clientX - this.dragOriginX);
    const range = this.dragRange;
    if (this.loop && (want < range.min || want > range.max)) {
      /* A looping strip has no end to push against. Swiper's loopFix
         re-orders the slides mid-drag; so does this — rotate() keeps the slide
         under the pointer exactly where it is and reports the scroll it
         corrected by, which the drag's origin absorbs. */
      const before = this.track.scrollLeft;
      this.rotate(this.currentIndex());
      const corrected = this.track.scrollLeft - before;
      this.dragOriginScroll += corrected;
      want += corrected;
    }
    const clamped = Math.min(range.max, Math.max(range.min, want));
    this.track.scrollLeft = clamped;
    /* Past the first or last slide of a strip that does not loop, Swiper lets
       the content keep coming, ever more reluctantly: the distance past the
       edge is shown raised to resistanceRatio (Swiper's own formula, less the
       1px it starts from). A scroll container cannot scroll past its content,
       so the overshoot is carried by the track's `translate` instead, and
       sprung back on release (bounceBack). */
    const over = want - clamped;
    const shown = Math.max(0, Math.pow(Math.abs(over), SWIPE.resistanceRatio) - 1);
    this.overshoot = over;
    this.track.style.translate = shown ? `${-Math.sign(over) * shown}px` : '';
  }

  /* Ends a press; returns true when a real swipe (not a tap) was just landed.
     `land` false ends it without choosing a slide — a tap, or a press the
     axis check gave to the page. */
  endDrag(event, land) {
    if (event.pointerId !== this.dragPointer) return false;
    if (this.track.hasPointerCapture(event.pointerId)) {
      this.track.releasePointerCapture(event.pointerId);
    }
    if (!land) {
      this.resetDragState(!this.tweenFrame);
      return false;
    }
    /* Swiper's onTouchEnd bails without choosing a slide when the strip never
       left its start point — no travel since the threshold re-based it, or
       back exactly where it began — unless a loop re-base happened on the
       way. The nearest slide is the same answer, and also the right one for
       a press that interrupted a glide mid-way. */
    const stayed =
      !this.loopSwapReset &&
      (this.diff === 0 || Math.abs(this.track.scrollLeft + this.overshoot - this.dragOriginScroll) < 0.5);
    const target = stayed ? this.currentIndex() : this.landing(performance.now() - this.pressTime);
    /* Snapping stays OFF here: handing it back now would re-snap the strip on
       the spot — to the NEAREST slide, in zero time, which is precisely the
       release the live site never makes. goTo() hands it back when the glide
       it starts has landed. */
    this.resetDragState(false);
    this.bounceBack();
    this.goTo(target);
    return true;
  }

  /* Swiper's onTouchEnd, verbatim in substance (lifted from the minified
     bundle the live site runs, not from the docs), over this strip's snap
     positions instead of Swiper's snapGrid:
     - find the slot the strip is in: the last snap position at or before the
       current one (every slot, when a strip that does not loop is at or past
       its end), and how far into it the strip has got, as a ratio;
     - a swipe that took longer than longSwipesMs moves on only past
       longSwipesRatio of the slot, in the direction it was going;
     - a shorter one ALWAYS moves one slot in its direction, however small —
       that is the flick a scroll-snap track cannot express.
     Returns a LOGICAL slide index for goTo(). */
  landing(elapsed) {
    const rtl = this.isRTL;
    const logical = (x) => (rtl ? -x : x);
    const trackRect = this.track.getBoundingClientRect();
    const inset = this.snapInset();
    const centered = this.isCentered(this.slides[0]);
    const scroll = this.track.scrollLeft;
    const maxLogical = this.dragRange.max - this.dragRange.min;
    /* Snap positions in track order, clamped to what the container can reach
       (Swiper's snapGrid likewise stops at maxTranslate): several trailing
       slides of a multi-per-view strip share the end position, and the first
       of them owns it. */
    const grid = [];
    const owners = [];
    this.domSlides().forEach((slide) => {
      const at = Math.min(maxLogical, Math.max(0, logical(scroll + this.offsetOf(slide, trackRect, inset, centered))));
      if (grid.length && Math.abs(at - grid[grid.length - 1]) < 0.5) return;
      grid.push(at);
      owners.push(slide);
    });
    const last = grid.length - 1;
    const pos = logical(scroll + this.overshoot);
    const atEnd = !this.loop && pos >= grid[last];
    let stop = 0;
    let size = last > 0 ? grid[1] - grid[0] : 1;
    for (let i = 0; i <= last; i += 1) {
      if (i < last) {
        if (atEnd || (pos >= grid[i] && pos < grid[i + 1])) {
          stop = i;
          size = grid[i + 1] - grid[i];
        }
      } else if (atEnd || pos >= grid[i]) {
        stop = i;
        size = last > 0 ? grid[last] - grid[last - 1] : 1;
      }
    }
    const ratio = (pos - grid[stop]) / (size || 1);
    /* swipeDirection as onTouchMove last left it (the travel from the start
       point, sign only); 'next' is forward. */
    const forward = this.swipeDirection === 'next';
    let slot;
    if (elapsed > SWIPE.longSwipesMs) {
      slot = forward
        ? (ratio >= SWIPE.longSwipesRatio ? stop + 1 : stop)
        : (ratio > 1 - SWIPE.longSwipesRatio ? stop + 1 : stop);
    } else {
      slot = forward ? stop + 1 : stop;
    }
    slot = Math.max(0, Math.min(last, slot));
    return this.slides.indexOf(owners[slot]);
  }

  /* Spring a pulled-past-the-edge strip back, over the same 300ms `ease` as
     any other move (Swiper's slideTo back to the edge). */
  bounceBack() {
    this.overshoot = 0;
    const from = parseFloat(this.track.style.translate) || 0;
    if (!from) return;
    if (prefersReducedMotion()) {
      this.track.style.translate = '';
      return;
    }
    const started = performance.now();
    const step = (now) => {
      const progress = Math.min(1, (now - started) / SLIDE_DURATION);
      if (progress < 1) {
        this.track.style.translate = `${from * (1 - easeCss(progress))}px`;
        this.bounceFrame = window.requestAnimationFrame(step);
        return;
      }
      this.bounceFrame = 0;
      this.track.style.translate = '';
    };
    this.bounceFrame = window.requestAnimationFrame(step);
  }

  /* Stops a spring-back where it stands; returns the offset it was showing. */
  stopBounce() {
    const shown = parseFloat(this.track.style.translate) || 0;
    if (this.bounceFrame) {
      window.cancelAnimationFrame(this.bounceFrame);
      this.bounceFrame = 0;
    }
    return shown;
  }

  /* The single place press state is cleared, so no caller can drop half of
     it. `restoreSnap` hands scroll-snap-type back to the stylesheet (x
     mandatory, and the browser then settles on the nearest slide) — which is
     wrong whenever a glide is about to start or is still running, so those
     callers pass false and the glide restores it when it lands. */
  resetDragState(restoreSnap = true) {
    this.dragPointer = -1; // pointerId of the live press (-1 = none)
    this.dragging = false; // true once the press has passed the threshold
    this.axisDecided = false;
    this.loopSwapReset = false;
    this.diff = 0;
    this.swipeDirection = null;
    this.touchesDirection = null;
    this.touchId = null;
    this.removeAttribute('data-dragging');
    if (restoreSnap) this.track.style.scrollSnapType = '';
  }

  unbindDrag() {
    if (!this.track || !this.onDragDown) return;
    this.track.removeEventListener('pointerdown', this.onDragDown);
    this.track.removeEventListener('pointermove', this.onDragMove);
    this.track.removeEventListener('touchstart', this.onTouchStart);
    this.track.removeEventListener('touchmove', this.onTouchMove);
    this.track.removeEventListener('pointerup', this.onDragUp);
    this.track.removeEventListener('pointercancel', this.onDragCancel);
    this.track.removeEventListener('lostpointercapture', this.onDragCancel);
    this.track.removeEventListener('dragstart', this.onDragImage);
    /* Armed or not, removing the one-shot click swallow is a no-op when it is
       not there — and the only way to be sure it never outlives the element.
       Its disarming timeout goes too, so it cannot fire against a track this
       element no longer owns. */
    this.track.removeEventListener('click', this.onDragClick, true);
    window.clearTimeout(this.dragClickTimer);
    /* Disconnecting mid-swipe would leave the host tagged [data-dragging]
       with snapping switched off and the track translated: reconnecting
       re-wires the listeners, but the pointerup that would have ended that
       swipe went with the old ones. The reachable case is detaching and
       re-inserting the SAME <snap-slider> (what the browser-check bootstraps
       do to force an overflowing strip). */
    this.stopBounce();
    this.overshoot = 0;
    this.track.style.translate = '';
    this.resetDragState();
  }

  /* --- Looping (data-loop) -------------------------------------------------
     The live media slider is a Swiper with `loop: true` + `centeredSlides`,
     and Swiper 9+ implements loop by REORDERING the real slides rather than
     cloning them: the captured page ships six slides in DOM order 3,4,5,0,1,2
     with slide 0 centred, i.e. the tail wrapped around to the left. Same idea
     here, for the same reason cloning is wrong in this theme — a clone would
     duplicate a <video>, a play button, and a theme-editor block anchor.

     `this.slides` therefore stays in LOGICAL (block) order for the whole life
     of the element: bullets, slider:change, data-start-index and the editor's
     shopify:block:select all keep addressing "the nth block". Only the track's
     child order rotates. Everything that reads geometry already goes through
     getBoundingClientRect(), so it follows the rotation for free; the places
     that assumed DOM order matched logical order (edgeState's first and last
     slide, currentIndex's end clamp, landing()'s snap positions) work in
     track order explicitly.

     A native scroll container cannot scroll past its content the way a
     transform can, so the strip must carry room on both sides at rest: that is
     what the centre gutters in base.css are for. They make every slide
     reachable at the centre snap point, which is also what a plain
     `scroll-snap-align: center` track needs to snap its first and last slide
     properly — the gutters are not a loop-only trick. */

  wrapIndex(index) {
    const n = this.slides.length;
    return ((index % n) + n) % n;
  }

  /* Slides in TRACK order (the rotation), as against this.slides' logical one. */
  domSlides() {
    return Array.from(this.track.children).filter((el) => el.hasAttribute('data-slider-slide'));
  }

  /* Does the strip have anywhere to go? Measured from the slides, not from
     scrollWidth, because a looping track's gutters make scrollWidth exceed
     clientWidth even when a single slide is all there is — and [data-static]
     ("nothing here can scroll") has to stay honest, since the gutters
     themselves are keyed off it. */
  overflows() {
    if (!this.loop) return this.track.scrollWidth - this.track.clientWidth > 1;
    const dom = this.domSlides();
    if (dom.length < 2) return false;
    const a = dom[0].getBoundingClientRect();
    const b = dom[dom.length - 1].getBoundingClientRect();
    return Math.max(a.right, b.right) - Math.min(a.left, b.left) > this.track.clientWidth + 1;
  }

  /* Rotate the track so slides[target] sits in the MIDDLE of the child order,
     leaving as much strip as possible on both sides of it. Nothing moves on
     screen: the slide that is currently at the snap point is measured before
     and after the re-parenting and scrollLeft is corrected by the difference,
     which is exact whatever the widths, gaps or writing direction (scrollLeft
     grows rightward in every current engine, RTL included — it just starts at
     0 and runs negative there).

     Returns the number of slides moved (signed), 0 when already in place. */
  rotate(target) {
    if (!this.loop || this.rotating || !this.hasAttribute('data-looping')) return 0;
    const dom = this.domSlides();
    const n = dom.length;
    const shift = Math.floor(n / 2) - dom.indexOf(this.slides[target]);
    if (!shift) return 0;

    this.rotating = true;
    /* The anchor is the slide the shopper is looking at, which is not always
       the target: sync() rotates around the slide already at the snap point,
       goTo() rotates before it has moved anywhere. */
    const anchor = this.slides[this.currentIndex()];
    const before = anchor.getBoundingClientRect().left;
    /* Mandatory snapping must not re-target the track between the DOM move and
       the scrollLeft correction — for those few statements the strip is
       deliberately in a position no snap point matches. */
    const snapWas = this.track.style.scrollSnapType;
    this.track.style.scrollSnapType = 'none';
    /* A slide can hold focus (its play button, a link in a caption). Measured:
       moveBefore keeps it, insertBefore drops it to <body> — so this restore
       is what makes moveInto()'s fallback path survivable, and a no-op on the
       moveBefore one. */
    const focused = this.track.contains(document.activeElement) ? document.activeElement : null;
    /* Moved against the filtered list, not firstElementChild/lastElementChild:
       a section is free to put something else in the track (a live region, a
       decorative element), and hopping a slide over it would reorder the
       markup rather than the carousel. */
    const order = dom.slice();
    for (let i = 0; i < Math.abs(shift); i += 1) {
      if (shift > 0) {
        const moved = order.pop();
        moveInto(this.track, moved, order[0]);
        order.unshift(moved);
      } else {
        const moved = order.shift();
        moveInto(this.track, moved, order[order.length - 1].nextSibling);
        order.push(moved);
      }
    }
    this.track.scrollLeft += anchor.getBoundingClientRect().left - before;
    this.track.style.scrollSnapType = snapWas;
    if (focused && document.activeElement !== focused) focused.focus({ preventScroll: true });
    this.rotating = false;
    return shift;
  }

  get isRTL() {
    return getComputedStyle(this.track).direction === 'rtl';
  }

  /* Scroll-padding shrinks the scrollport into the SNAPPORT, so a correctly
     snapped slide sits inset from the raw track edge (the testimonial track
     uses it to recreate the page gutter). Physical properties are read and
     picked by direction — logical ones are not reliably resolved here. */
  snapInset() {
    const styles = getComputedStyle(this.track);
    const rtl = this.isRTL;
    return {
      /* 'auto' parses to NaN → 0, i.e. today's behaviour */
      start: parseFloat(rtl ? styles.scrollPaddingRight : styles.scrollPaddingLeft) || 0,
      end: parseFloat(rtl ? styles.scrollPaddingLeft : styles.scrollPaddingRight) || 0,
    };
  }

  /* Where the browser will actually park a slide, read from the RESOLVED
     scroll-snap-align rather than from data-align.

     The attribute is the CSS input (base.css turns data-align="center" into
     scroll-snap-align: center), but a section is free to override the axis at
     a breakpoint — and one does: the testimonial slider is start-aligned from
     768 up and CENTER-aligned below it, matching the source's centeredSlides
     true/false split. Trusting the attribute there made slider.js measure
     against the start edge while the browser snapped to the middle; measured
     at 390px the resting slide sat 19.86px from the track edge where the
     start metric expected the 16px scroll-padding. Reading the computed value
     keeps the two in step at every breakpoint, for free.

     Two values mean <block> <inline>; the inline one is the last token. */
  isCentered(slide) {
    const value = getComputedStyle(slide).scrollSnapAlign.trim().split(/\s+/);
    return value[value.length - 1] === 'center';
  }

  /* Signed offset of a slide from the snap point (start edge or center).
     In RTL the logical start edge is the RIGHT edge — rect-based, so the
     result is a physical scrollBy delta in both directions.
     `centered` is hoisted by callers that loop over every slide, so the
     computed-style read happens once per pass rather than once per slide. */
  offsetOf(slide, trackRect, inset, centered) {
    const pad = inset || this.snapInset();
    const rect = slide.getBoundingClientRect();
    if (centered === undefined ? this.isCentered(slide) : centered) {
      const shift = ((pad.start - pad.end) / 2) * (this.isRTL ? -1 : 1);
      return rectCenter(rect) - (rectCenter(trackRect) + shift);
    }
    return this.isRTL ? rect.right - (trackRect.right - pad.start) : rect.left - (trackRect.left + pad.start);
  }

  /* Is the strip resting against either end? Rect-based, never raw scrollLeft
     (whose sign differs across browsers in RTL); the track's own padding is
     discounted so a padded track still reads as "at the edge". The 1px slack
     absorbs sub-pixel layout. */
  edgeState(trackRect) {
    const rect = trackRect || this.track.getBoundingClientRect();
    const styles = getComputedStyle(this.track);
    const rtl = this.isRTL;
    const padStart = parseFloat(rtl ? styles.paddingRight : styles.paddingLeft) || 0;
    const padEnd = parseFloat(rtl ? styles.paddingLeft : styles.paddingRight) || 0;
    /* Track order, not logical order: under a loop rotation slides[0] is
       somewhere in the middle of the strip, and asking whether IT has reached
       the left edge answers a different question than "is the strip at its
       end". Identical for every other slider, where the two orders match. */
    const dom = this.domSlides();
    const firstRect = dom[0].getBoundingClientRect();
    const lastRect = dom[dom.length - 1].getBoundingClientRect();
    return {
      atStart: rtl
        ? firstRect.right <= rect.right - padStart + 1
        : firstRect.left >= rect.left + padStart - 1,
      atEnd: rtl
        ? lastRect.left >= rect.left + padEnd - 1
        : lastRect.right <= rect.right - padEnd + 1,
    };
  }

  /* --- Mousewheel (opt-in, data-wheel) -------------------------------------
     Swiper's mousewheel module, which the source runs in two shapes (see the
     header): data-wheel="horizontal" answers only sideways intent
     (forceToAxis), bare data-wheel answers either axis. In both, a wheel
     gesture is a COMMAND, not a distance: the first event of a gesture moves
     one slide over the usual 300ms, and the rest of that gesture is ignored —
     Swiper only acts again on an event that changes direction, is bigger than
     the one before it, or comes more than 150ms after it, and even then not
     within 60ms of its last step. Measured on the live strips: one 100px
     notch = one slide, a trackpad burst of six 40px events = one slide. A
     native scroll container instead scrolled the notch's 100px and snapped
     straight back, so a horizontal wheel never moved the strip at all. */
  bindWheel() {
    this.wheelHistory = [];
    this.wheelStepAt = 0;
    this.onWheel = (event) => {
      /* A pinch-zoom gesture arrives as ctrl+wheel; that belongs to the page. */
      if (event.ctrlKey) return;
      if (!this.overflows()) return;
      /* Swiper's normalisation: LINE mode (Firefox's classic wheel) is 40px a
         line and PAGE mode 800px a page, and shift+wheel is horizontal. */
      const unit = event.deltaMode === 1 ? 40 : event.deltaMode === 2 ? 800 : 1;
      let pixelX = event.deltaX * unit;
      let pixelY = event.deltaY * unit;
      if (event.shiftKey && !pixelX) {
        pixelX = pixelY;
        pixelY = 0;
      }
      const flip = this.isRTL ? -1 : 1;
      let delta;
      if (this.dataset.wheel === 'horizontal') {
        /* Vertical intent is the page's: return without preventDefault. */
        if (Math.abs(pixelX) <= Math.abs(pixelY)) return;
        delta = -pixelX * flip;
      } else {
        delta = Math.abs(pixelX) > Math.abs(pixelY) ? -pixelX * flip : -pixelY;
      }
      if (!delta) return;
      const wheel = { time: performance.now(), delta: Math.abs(delta), direction: Math.sign(delta) };
      const previous = this.wheelHistory[this.wheelHistory.length - 1];
      if (this.wheelHistory.length >= 2) this.wheelHistory.shift();
      this.wheelHistory.push(wheel);
      if (
        !previous ||
        wheel.direction !== previous.direction ||
        wheel.delta > previous.delta ||
        wheel.time > previous.time + 150
      ) {
        this.wheelStep(wheel);
      }
      /* Release at the ends (releaseOnEdges): a strip that has nowhere left
         to go in that direction gives the wheel back to the page. The source
         sets this on every strip except the PDP thumbnails, whose bare
         `mousewheel: true` leaves Swiper's default (false) — so its thumb row
         keeps swallowing the wheel once it has bottomed out, and a shopper
         scrolling the page with the cursor over the thumbnails gets stuck
         there. That is a real usability problem, so the thumbnails follow the
         other strips here: a DELIBERATE deviation, not parity. */
      if (!this.loop) {
        const { atStart, atEnd } = this.edgeState();
        if ((wheel.direction < 0 && atEnd) || (wheel.direction > 0 && atStart)) return;
      }
      event.preventDefault();
    };
    /* passive:false — preventDefault is the whole point. */
    this.track.addEventListener('wheel', this.onWheel, { passive: false });
  }

  /* Swiper's animateSlider(): negative delta = forward (content travels
     toward the start), and a strip that is still gliding ignores the step. */
  wheelStep(wheel) {
    if (wheel.delta >= 6 && wheel.time - this.wheelStepAt < 60) return;
    if (!this.tweenFrame) {
      const { atStart, atEnd } = this.edgeState();
      if (wheel.direction < 0) {
        if (this.loop || !atEnd) this.step(1);
      } else if (this.loop || !atStart) {
        this.step(-1);
      }
    }
    this.wheelStepAt = wheel.time;
  }

  unbindWheel() {
    if (this.track && this.onWheel) this.track.removeEventListener('wheel', this.onWheel);
  }

  /* Index of the slide closest to the snap point (start or center). */
  currentIndex() {
    const trackRect = this.track.getBoundingClientRect();
    const inset = this.snapInset();
    /* A slide whose snap offset falls outside [0, maxScroll] can never reach the
       snap point, so nearest-snap alone would never report the LAST slide: its
       bullet would never take aria-current and media-slider's last caption would
       stay visibility:hidden for the whole session. Detect the end from rects,
       never from raw scrollLeft (whose sign differs across browsers in RTL).
       The start extreme is deliberately NOT clamped: with data-align="center"
       and several slides in view, resting at scrollLeft 0 leaves the first slide
       at the track edge, not at the snap point, so nearest-snap is the honest
       answer there. */
    const { atStart, atEnd } = this.edgeState(trackRect);
    const centered = this.isCentered(this.slides[0]);
    let best = 0;
    let bestDistance = Infinity;
    this.slides.forEach((slide, index) => {
      const distance = Math.abs(this.offsetOf(slide, trackRect, inset, centered));
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });
    /* Only clamp when NOTHING reaches the snap point here: with an odd number of
       slides in view the last reachable centre-snap lands exactly on max scroll,
       and that slide is the honest answer (media-slider is 3/view at 640-975px,
       where clamping would dim a fully centered card and hide its caption). */
    if (atEnd && !atStart && bestDistance > 1 && !this.loop) return this.slides.length - 1;
    return best;
  }

  goTo(index, behavior) {
    /* A looping strip has no first or last slide to clamp against — step(-1)
       off slide 0 means slide n-1 — and the move is measured only AFTER the
       target has been rotated into the middle, so the delta is always the
       short way round and always inside the scroll range. */
    const clamped = this.loop
      ? this.wrapIndex(index)
      : Math.max(0, Math.min(this.slides.length - 1, index));
    /* Stop a glide in flight where it stands — WITHOUT handing snapping back:
       that would re-snap the strip on the spot, a visible jump to the nearest
       slide before this move has even started (a double press on an arrow,
       two quick wheel notches). Every exit below restores it. Stopped before
       rotate(), which reads and writes scrollLeft that a still-running frame
       would then overwrite. */
    this.stopTweenFrame();
    if (this.loop) this.rotate(clamped);
    const delta = this.offsetOf(this.slides[clamped], this.track.getBoundingClientRect(), this.snapInset());
    /* 'auto' = jump. scrollLeft updates synchronously, so the state can be
       re-derived immediately — including when the container clamped the delta
       away and no scroll event will ever fire.
       Sub-pixel deltas are noise — the strip is already on the snap point — and
       acting on them is actively harmful: Blink reads any scrollBy on a
       mandatory-snap container as a scroll in that DIRECTION and answers it
       with the next snap point along, so a -0.5px correction walks a centred
       slide a whole slot backwards (measured on the looping media slider,
       whose 337.5px slides make half-pixel offsets the norm). Anything a
       shopper asked for is a slot wide; nothing real is lost here. */
    if (prefersReducedMotion() || behavior === 'auto' || Math.abs(delta) < 1) {
      if (Math.abs(delta) >= 1) this.track.scrollBy({ left: delta, behavior: 'auto' });
      this.track.style.scrollSnapType = '';
      this.sync();
      return;
    }
    /* Publish the destination NOW, at the start of the glide: Swiper's slideTo
       moves activeIndex (and the slide classes, and fires slideChange) before
       its transition runs, so on the live media strip the dimming and the
       caption cards swap the moment a swipe is released. Waiting for the glide
       to land plus the 80ms scroll debounce put that swap ~380ms late. */
    this.render(clamped);
    this.tweenBy(delta);
  }

  /* Scroll by a fixed delta over SLIDE_DURATION on the CSS `ease` curve, the
     way Swiper's wrapper transition does. */
  tweenBy(delta) {
    /* Read the origin BEFORE standing any running tween down. A second
       command inside the 300ms window (a double-click on prev/next, two quick
       bullet presses, a thumb click while a variant tween is in flight) must
       start from where the strip actually is. */
    const from = this.track.scrollLeft;
    /* Aim at what the container can actually reach. A slide past the end of
       a strip that does not loop (the second comparison card, the last
       testimonial) snaps at max scroll, not at its own offset — tweening
       toward the unreachable offset ran the ease into the wall: the strip
       stopped dead after ~200ms instead of easing out over the full 300ms
       (measured: 114–230ms on the theme, ~300–330ms on the live Swiper,
       which clamps its target to maxTranslate before animating). */
    const max = this.track.scrollWidth - this.track.clientWidth;
    const rtl = this.isRTL;
    const to = Math.min(rtl ? 0 : max, Math.max(rtl ? -max : 0, from + delta));
    if (Math.abs(to - from) < 1) {
      this.stopTweenFrame();
      this.track.style.scrollSnapType = '';
      this.sync();
      return;
    }
    this.stopTweenFrame();
    /* Mandatory snapping retargets every programmatic scroll to the nearest
       snap point, so it would fight the tween frame by frame. Stood down for
       the duration; the inline value is cleared on completion and the browser
       then settles on the slide the tween landed on. */
    this.track.style.scrollSnapType = 'none';
    const started = performance.now();
    const step = (now) => {
      const progress = Math.min(1, (now - started) / SLIDE_DURATION);
      this.track.scrollLeft = from + (to - from) * easeCss(progress);
      if (progress < 1) {
        this.tweenFrame = window.requestAnimationFrame(step);
        return;
      }
      this.tweenFrame = 0;
      this.track.style.scrollSnapType = '';
      this.sync();
    };
    this.tweenFrame = window.requestAnimationFrame(step);
  }

  /* Cancels the glide's next frame; returns whether one was running. */
  stopTweenFrame() {
    if (!this.tweenFrame) return false;
    window.cancelAnimationFrame(this.tweenFrame);
    this.tweenFrame = 0;
    return true;
  }

  /* Ends a glide for good — never leaving snapping switched off behind it. */
  cancelTween() {
    if (!this.stopTweenFrame()) return;
    this.track.style.scrollSnapType = '';
  }

  /* Slide indexes follow DOM/reading order, which is already
     direction-neutral — no RTL negation here (goTo produces the correct
     physical delta from rects). Mid-glide the strip is between two slides,
     so the step is taken from the one it is already heading to, as Swiper's
     slideNext does from activeIndex: a double press advances twice instead
     of re-aiming at the same slide. */
  step(direction) {
    const from = this.tweenFrame && Number.isInteger(this.index) ? this.index : this.currentIndex();
    this.goTo(from + direction);
  }

  sync() {
    /* Under a pointer, or mid-glide, the strip is between slides on purpose,
       and the live strip does not re-tag its slides until the release: the
       release publishes its destination (goTo → render), and the glide's last
       frame calls back here to confirm it. This is also what keeps rotate()
       from re-ordering the track under a finger. */
    if (this.dragging || this.tweenFrame) return;
    const index = this.currentIndex();
    /* Re-centre the rotation on whatever the shopper landed on, so there is
       again a full strip on both sides of it. Deliberately here, at the tail
       of the 80 ms scroll debounce, and not on every scroll event: writing
       scrollLeft mid-fling cancels the fling in Blink, and the correction is
       invisible only because the strip has already come to rest. */
    if (this.loop) this.rotate(index);
    this.render(index);
  }

  /* Tag the slides, bullets and arrows for `index` and announce it. */
  render(index) {
    this.index = index;
    const last = this.slides.length - 1;
    /* Neighbours wrap when looping — the media slider lifts [data-prev] /
       [data-next] out of the 50% dim and shows their caption, and on the live
       site that trio is the three centre slides of the rotation, so at rest it
       is the LAST block that sits to the left of the first one. */
    const prev = this.loop && index === 0 ? last : index - 1;
    const next = this.loop && index === last ? 0 : index + 1;
    this.slides.forEach((slide, i) => {
      slide.toggleAttribute('data-active', i === index);
      slide.toggleAttribute('data-prev', i === prev);
      slide.toggleAttribute('data-next', i === next);
    });
    if (this.bullets) {
      Array.from(this.bullets.children).forEach((bullet, i) => {
        bullet.setAttribute('aria-current', String(i === index));
      });
    }
    /* A track that does not overflow shows every slide at once, so any styling
       that dims or hides the non-current ones (media-slider's captions) has
       nothing left to reveal and must stand down. Re-evaluated on every sync,
       so the ResizeObserver keeps it right across breakpoints. */
    const canScroll = this.overflows();
    this.toggleAttribute('data-static', !canScroll);
    this.toggleAttribute('data-looping', this.loop && canScroll);
    /* A loop has no ends to run out of, so its arrows never go dead. */
    if (this.prevButton) this.prevButton.toggleAttribute('disabled', !this.loop && index === 0);
    if (this.nextButton) this.nextButton.toggleAttribute('disabled', !this.loop && index === last);
    this.dispatchEvent(new CustomEvent('slider:change', { detail: { index } }));
  }

  buildBullets() {
    this.bullets.innerHTML = '';
    this.slides.forEach((slide, index) => {
      const bullet = document.createElement('button');
      bullet.type = 'button';
      bullet.className = 'slider-bullet';
      /* The localized label template comes from the markup (data-bullet-label,
         with [index]/[total] placeholders) — never hardcode shopper strings
         here. Sections that do not pass one keep the old numeric label. */
      const template = this.bullets.dataset.bulletLabel;
      bullet.setAttribute(
        'aria-label',
        template
          ? template.replace('[index]', index + 1).replace('[total]', this.slides.length)
          : `${index + 1} / ${this.slides.length}`
      );
      bullet.addEventListener('click', () => this.goTo(index));
      this.bullets.appendChild(bullet);
    });
  }
}
if (!customElements.get('snap-slider')) customElements.define('snap-slider', SnapSlider);

/* Theme editor: selecting a block whose slide is off-screen must bring that
   slide to the snap point. Delegated + generic, so any section whose slides
   (or blocks inside them) carry [data-slider-slide] works with no extra code. */
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:block:select', (event) => {
    const target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    const slide = target.closest('[data-slider-slide]');
    if (!slide) return;
    const slider = slide.closest('snap-slider');
    if (!slider || typeof slider.goTo !== 'function' || !slider.slides) return;
    const index = slider.slides.indexOf(slide);
    if (index < 0) return;
    /* A pending data-start-index frame would run after us and undo the jump. */
    if (slider.startFrame) {
      window.cancelAnimationFrame(slider.startFrame);
      slider.startFrame = 0;
    }
    slider.goTo(index, event.detail && event.detail.load ? 'auto' : undefined);
  });
}

/* --- <media-slide> — click-to-play video card used by the UGC slider ------- */
class MediaSlide extends HTMLElement {
  connectedCallback() {
    /* A looping <snap-slider> re-parents whole slides to rotate the strip, and
       BOTH of the primitives it can use for that run this callback again: the
       atomic-move spec skips the disconnected/connected pair only for elements
       implementing connectedMoveCallback, which this is not, so moveBefore is
       as talkative here as insertBefore (measured: one of each, either way).
       Wiring twice would double every play/pause handler — two pauses per
       click, the second one restoring the overlay the first had just hidden.
       The flag lives on the element, so it survives the move exactly as the
       listeners do (nothing here is torn down on disconnect). */
    if (this.bound) return;
    this.video = this.querySelector('video');
    this.overlay = this.querySelector('[data-video-overlay]');
    if (!this.video || !this.overlay) return;
    this.bound = true;

    this.overlay.addEventListener('click', () => {
      if (!this.video.paused) {
        this.video.pause();
        return;
      }
      const played = this.video.play();
      /* An interrupted (double click, or the mutual-exclusion pause below) or
         blocked play() rejects; the 'pause' handler already restores the
         overlay, so swallow the rejection instead of leaking it. */
      if (played && typeof played.catch === 'function') played.catch(() => {});
    });
    this.video.addEventListener('play', () => {
      /* Section CSS display:none's the overlay once [data-playing] lands, so
         make the video focusable FIRST and hand focus over — otherwise the
         focused button disappears and focus drops to <body>. */
      const overlayHadFocus =
        this.overlay === document.activeElement || this.overlay.contains(document.activeElement);
      this.video.setAttribute('controls', '');
      this.setAttribute('data-playing', '');
      if (overlayHadFocus) this.video.focus();
      /* Starting one video pauses every other one (brand behavior). */
      document.querySelectorAll('media-slide[data-playing]').forEach((other) => {
        if (other !== this && other.video && !other.video.paused) other.video.pause();
      });
    });
    this.video.addEventListener('pause', () => {
      /* Native controls report the host <video> as the active element. */
      const videoHadFocus = document.activeElement === this.video;
      this.removeAttribute('data-playing');
      /* Rewinding to the poster frame and dropping the native controls is the
         UGC slider's brand behavior — opt-in, so galleries that reuse this
         element keep their controls and playback position. */
      if (!this.hasAttribute('data-rewind-on-pause')) return;
      this.video.removeAttribute('controls');
      this.video.currentTime = 0;
      /* Re-run resource selection: seeking alone does not set the show-poster
         flag again, so without this the card shows frame 0, not the poster. */
      this.video.load();
      if (videoHadFocus) this.overlay.focus();
    });
  }
}
if (!customElements.get('media-slide')) customElements.define('media-slide', MediaSlide);
